import React from "react";
import useHotWorkOnShipPermitForm from "../../../hooks/useHotWorkOnShipPermitForm";
import { AnonymousPermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import LoadingSpinner from "../../Common/LoadingSpinner";
import PermitHeader from "./PermitHeader";
import { PermitType } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import TextInput from "../../Common/TextInput";
import TextArea from "../../Common/TextArea";
import LabelCheckbox from "../../Common/LabelCheckbox";
import AdminComment from "./AdminComment";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import Button from "../../Common/Button";
import SignatureModal from "../../Signatures/SignatureModal";
import RejectPermitModal from "../RejectPermitModal";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import UpdateWithNotificationModal from "./UpdateWithNotificationModal";
import PositionPicker from "./PositionPicker";

function HotWorkOnShipPermitForm(props: AnonymousPermitFormProps) {
    const formManager = useHotWorkOnShipPermitForm(props);

    const {
        formState,
        isLoading,
        storedPermit,
        department,
        formStateChangeHandler
    } = formManager;
    const { resources } = props;

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={resources.get("HotWorkOnShipPermit")}
                            permitType={PermitType.HotWorkOnShipPermit}
                            resources={resources}
                        />
                    </div>

                    {!!storedPermit && formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <div className="mt-3 mb-3">
                        <h1 className="h6">{resources.get("PermitProcedure")}</h1>
                        <p>{resources.get("ConditionsInformation")}</p>
                    </div>

                    <form onSubmit={formManager.submitHandler}>
                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.shipName.value}
                                errorMessage={formState.shipName.errorMessage}
                                inputId="ship-name"
                                labelText={props.resources.get("ShipName")}
                                onChange={(s) => formStateChangeHandler("shipName", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.callSign.value}
                                errorMessage={formState.callSign.errorMessage}
                                inputId="call-sign"
                                labelText={props.resources.get("CallSign")}
                                onChange={(s) => formStateChangeHandler("callSign", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                value={formState.description.value}
                                errorMessage={formState.description.errorMessage}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="commander-or-representative__name"
                                containerClassName="col-md-6"
                                value={formState.commanderOrRepresentative.name}
                                errorMessage={formState.commanderOrRepresentative.nameErrorMessage}
                                labelText={resources.get("CommanderOrRepresentative")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("commanderOrRepresentative", {
                                        ...formState.commanderOrRepresentative,
                                        name: s
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                inputId="commander-or-representative__phone-number"
                                containerClassName="col-md-6"
                                value={formState.commanderOrRepresentative.phoneNumber}
                                errorMessage={formState.commanderOrRepresentative.phoneNumberErrorMessage}
                                labelText={resources.get("CommanderOrRepresentativePhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("commanderOrRepresentative", {
                                        ...formState.commanderOrRepresentative,
                                        phoneNumber: s
                                    })
                                }
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <LabelCheckbox
                            checked={formState.terminalAcceptsWork.value}
                            id="terminal-accepts-work"
                            label={resources.get("TerminalAcceptsWork")}
                            onChange={(c) => formStateChangeHandler("terminalAcceptsWork", c)}
                            containerClassName="mt-3"
                                readOnly={!formManager.canManageContent}
                        />

                        <LabelCheckbox
                            checked={formState.ehCoordinatorAcceptsWork.value}
                            id="eh-coordinator-accepts-work"
                            label={resources.get("EHCoordinatorAcceptsWork")}
                            onChange={(c) => formStateChangeHandler("ehCoordinatorAcceptsWork", c)}
                                readOnly={!formManager.canManageContent}
                        />

                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-5"
                        />

                        {formManager.showAnonymousUserEmail ? (
                            <TextInput
                                containerClassName="mt-3 col-md-6"
                                inputId="anonymous-user-email"
                                labelText={resources.get("AnonymousUserEmail")}
                                onChange={(s) => formStateChangeHandler("anonymousUserEmail", s)}
                                value={formState.anonymousUserEmail.value}
                                errorMessage={formState.anonymousUserEmail.errorMessage}
                                readOnly={!formManager.canManageContent}
                            />
                        ) : null}

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        <div className="mt-3">
                            {formManager.canCreate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Send")}
                                </Button>
                            ) : formManager.canUpdate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Update")}
                                </Button>
                            ) : null}
                            {formManager.canUpdateWithNotification ? (
                                <Button
                                    type="button"
                                    className="ms-2"
                                    onClick={() => formManager.setShowUpdateWithNotificationModal(true)}
                                >
                                    {resources.get("UpdateWithNotification")}
                                </Button>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                onReject={formManager.rejectPermitHandlerAsync}
                resources={resources}
                show={formManager.showRejectPermitModal}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <UpdateWithNotificationModal
                onHide={() => formManager.setShowUpdateWithNotificationModal(false)}
                show={formManager.showUpdateWithNotificationModal}
                onUpdate={formManager.updateWithNotificationHandlerAsync}
                permitDocument={formManager.storedPermit}
                resources={resources}
            />
        </>
    );
}

export default HotWorkOnShipPermitForm;
